import React, { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import {
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { isStripeErrorInputField } from 'src/state-machines/purchase/functions/is-stripe-error-input-field';
import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { RSNote } from 'src/reservation-page/RSNote';

import { SectionFieldset } from 'src/organisms/product-purchase/SectionFieldset';
import { PaymentMethodInputNewCreditCard } from 'src/organisms/product-purchase/PaymentMethodInputNewCreditCard';
import { PaymentMethodInput } from 'src/organisms/product-purchase/PaymentMethodInput';

import { FormValues } from '../..';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';

export interface PaymentMethodFieldsetProps {
  user: ReservationFlowMe;
  isJCBEnabled: boolean;
}

export const PaymentMethodFieldset: FC<PaymentMethodFieldsetProps> = ({
  user,
  isJCBEnabled,
}) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const [, send] = ProductPurchaseServiceContext.useActor();

  const dataStripeError = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.stripeError,
  );

  const { control, setValue } = useFormContext<FormValues>();

  const watchedPaymentMethod = useWatch({
    control,
    name: 'paymentMethod',
  });

  useEffect(() => {
    if (typeof watchedPaymentMethod === 'undefined') return;

    send({
      type: 'INPUT_PAYMENT_METHOD',
      payload: { paymentMethod: watchedPaymentMethod },
    });
  }, [send, watchedPaymentMethod]);

  useEffect(() => {
    stripe &&
      send({
        type: 'SET_STRIPE_INSTANCE',
        payload: { stripeV3Instance: stripe },
      });
  }, [send, stripe]);

  const handlePaymentElementChange = async (
    event: StripePaymentElementChangeEvent,
  ) => {
    if (!stripe || !elements || !event.complete) {
      send({ type: 'RESET_STRIPE_PAYMENT_ELEMENTS' });
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      send({ type: 'RESET_STRIPE_PAYMENT_ELEMENTS' });
      return;
    }
    setValue('paymentMethod', {
      type: 'new_credit_card',
      // TODO: [3ds] legacy排除後はこの形で保持しなくても良い
      creditCardInfo: {},
      isStoreCreditCard: (
        watchedPaymentMethod as ReservationFlowSelectedPaymentMethodNewCreditCard
      )?.isStoreCreditCard,
    });

    send({
      type: 'SET_STRIPE_PAYMENT_ELEMENTS',
      payload: { stripeElements: elements },
    });
  };

  return (
    <SectionFieldset
      legend={intl.formatMessage({
        defaultMessage: 'お支払い方法',
        id: 'wjKV6l',
        description:
          'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
      })}
    >
      {typeof dataStripeError !== 'undefined' &&
        !isStripeErrorInputField(dataStripeError) && (
          <div className="mb-2.5">
            <div>
              {(() => {
                switch (dataStripeError.code) {
                  case 'card_declined':
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            '利用不可のカード番号です。今一度ご確認ください。',
                          id: 'TH+4na',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                  case 'expired_card':
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            '有効期限切れのカード番号です。今一度ご確認ください。',
                          id: '4Dm69k',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                  default:
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            'クレジットカード決済ができませんでした。今一度クレジットカード情報を確認してください。',
                          id: 'qWJvJ5',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                }
              })()}
            </div>
          </div>
        )}

      {(() => {
        if (!user.credit_card) {
          return (
            <Controller
              control={control}
              name="paymentMethod"
              render={({ field: { value, onChange, onBlur, ref } }) => {
                return (
                  <PaymentMethodInputNewCreditCard
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    stripeError={dataStripeError}
                    currentYear={dayjs().format('YYYY')}
                    isJCBEnabled={isJCBEnabled}
                    isMonthlyBilling={false}
                    isLegacyForm={false}
                    handlePaymentElementChange={handlePaymentElementChange}
                  />
                );
              }}
            />
          );
        }

        return (
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <PaymentMethodInput
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  stripeError={dataStripeError}
                  userCreditCard={user.credit_card}
                  currentYear={dayjs().format('YYYY')}
                  isJCBEnabled={isJCBEnabled}
                  isMonthlyBilling={false}
                  isLegacyForm={false}
                  handlePaymentElementChange={handlePaymentElementChange}
                />
              );
            }}
          />
        );
      })()}
    </SectionFieldset>
  );
};
